






























































































import _ from "lodash";
import { mathUtils, runModelUtils } from "../../utils";
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { Tensor, InferenceSession } from "onnxruntime-web";
import ModelStatus from "../common/ModelStatus.vue";

@Component({
  components: {
    ModelStatus,
  },
})
export default class DrawingModelUI extends Vue {
  @Prop({ type: String, required: true }) modelFilepath!: string;
  @Prop({ type: Function, required: true }) preprocess!: (
    ctx: CanvasRenderingContext2D
  ) => Tensor;
  @Prop({ type: Function, required: true }) postprocess!: (
    t: Tensor
  ) => Float32Array;
  @Prop({ type: Function, required: true }) getPredictedClass!: (
    output: Float32Array
  ) => number;

  modelLoading: boolean;
  modelInitializing: boolean;
  modelLoadingError: boolean;
  sessionRunning: boolean;
  input: Float32Array;
  output: Float32Array;
  outputClasses: number[];
  drawing: boolean;
  strokes: number[][][];
  inferenceTime: number;
  session: InferenceSession;
  gpuSession: InferenceSession | undefined;
  cpuSession: InferenceSession | undefined;
  sessionBackend: string;
  modelFile: ArrayBuffer;
  backendSelectList: Array<{ text: string; value: string }>;

  constructor() {
    super();
    this.input = new Float32Array(784);
    this.output = new Float32Array(10);
    this.outputClasses = _.range(10);
    this.drawing = false;
    this.strokes = [];
    this.inferenceTime = 0;
    this.modelLoading = true;
    this.modelInitializing = true;
    this.sessionRunning = false;
    this.modelLoadingError = false;
    this.sessionBackend = "webgl";
    this.modelFile = new ArrayBuffer(0);
    this.backendSelectList = [
      { text: "GPU-WebGL", value: "webgl" },
      { text: "CPU-WebAssembly", value: "wasm" },
    ];
  }

  async created() {
    // fetch the model file to be used later
    const response = await fetch(this.modelFilepath);
    this.modelFile = await response.arrayBuffer();
    try {
      await this.initSession();
    } catch (e) {
      this.sessionBackend = "wasm";
    }
  }

  async initSession() {
    this.sessionRunning = false;
    this.modelLoadingError = false;
    if (this.sessionBackend === "webgl") {
      if (this.gpuSession) {
        this.session = this.gpuSession;
        return;
      }
      this.modelLoading = true;
      this.modelInitializing = true;
    }
    if (this.sessionBackend === "wasm") {
      if (this.cpuSession) {
        this.session = this.cpuSession;
        return;
      }
      this.modelLoading = true;
      this.modelInitializing = true;
    }

    try {
      if (this.sessionBackend === "webgl") {
        this.gpuSession = await runModelUtils.createModelGpu(this.modelFile);
        this.session = this.gpuSession;
      } else if (this.sessionBackend === "wasm") {
        this.cpuSession = await runModelUtils.createModelCpu(this.modelFile);
        this.session = this.cpuSession;
      }
    } catch (e) {
      this.modelLoading = false;
      this.modelInitializing = false;
      if (this.sessionBackend === "webgl") {
        this.gpuSession = undefined;
      } else {
        this.cpuSession = undefined;
      }
      throw new Error("Error: Backend not supported. ");
    }
    this.modelLoading = false;
    // warm up session with a sample tensor. Use setTimeout(..., 0) to make it an async execution so
    // that UI update can be done.
    if (this.sessionBackend === "webgl") {
      setTimeout(() => {
        runModelUtils.warmupModel(this.session!, [1, 1, 28, 28]);
        this.modelInitializing = false;
      }, 0);
    } else {
      await runModelUtils.warmupModel(this.session!, [1, 1, 28, 28]);
      this.modelInitializing = false;
    }
  }

  @Watch("sessionBackend")
  async onSessionBackendChange(newVal: string) {
    this.sessionBackend = newVal;
    this.clear();
    try {
      await this.initSession();
    } catch (e) {
      this.modelLoadingError = true;
    }
    return newVal;
  }

  async run() {
    if (!this.drawing) {
      return;
    }
    this.drawing = false;
    this.sessionRunning = true;
    const ctx = (
      document.getElementById("input-canvas") as HTMLCanvasElement
    ).getContext("2d") as CanvasRenderingContext2D;
    const tensor = this.preprocess(ctx);
    const [res, time] = await runModelUtils.runModel(this.session, tensor);
    this.output = this.postprocess(res);
    this.inferenceTime = time;
    this.sessionRunning = false;
  }

  get predictedClass() {
    return this.getPredictedClass(this.output);
  }

  clear() {
    const ctx = (
      document.getElementById("input-canvas") as HTMLCanvasElement
    ).getContext("2d") as CanvasRenderingContext2D;
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    const ctxCenterCrop = (
      document.getElementById("input-canvas-centercrop") as HTMLCanvasElement
    ).getContext("2d") as CanvasRenderingContext2D;
    ctxCenterCrop.clearRect(
      0,
      0,
      ctxCenterCrop.canvas.width,
      ctxCenterCrop.canvas.height
    );
    const ctxScaled = (
      document.getElementById("input-canvas-scaled") as HTMLCanvasElement
    ).getContext("2d") as CanvasRenderingContext2D;
    ctxScaled.clearRect(0, 0, ctxScaled.canvas.width, ctxScaled.canvas.height);
    this.output = new Float32Array(10);
    this.drawing = false;
    this.strokes = [];
  }

  activateDraw(e: any) {
    if (this.modelLoading || this.modelInitializing || this.modelLoadingError) {
      return;
    }
    this.drawing = true;
    this.strokes.push([]);
    const points = this.strokes[this.strokes.length - 1];
    points.push(mathUtils.getCoordinates(e));
    this.draw(e);
  }

  draw(e: any) {
    if (!this.drawing) {
      return;
    }
    // disable scrolling behavior when drawing
    e.preventDefault();
    const ctx = (
      document.getElementById("input-canvas") as HTMLCanvasElement
    ).getContext("2d") as CanvasRenderingContext2D;
    ctx.lineWidth = 20;
    ctx.lineJoin = ctx.lineCap = "round";
    ctx.strokeStyle = "#393E46";
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    let points = this.strokes[this.strokes.length - 1];
    points.push(mathUtils.getCoordinates(e));
    // draw individual strokes
    for (let s = 0, slen = this.strokes.length; s < slen; s++) {
      points = this.strokes[s];
      let p1 = points[0];
      let p2 = points[1];
      ctx.beginPath();
      ctx.moveTo(p1[0], p1[1]);
      // draw points in stroke
      // quadratic bezier curve
      for (let i = 1, len = points.length; i < len; i++) {
        const midpoint = mathUtils.getMidpoint(p1, p2);
        ctx.quadraticCurveTo(p1[0], p1[1], midpoint[0], midpoint[1]);
        p1 = points[i];
        p2 = points[i + 1];
      }
      ctx.lineTo(p1[0], p1[1]);
      ctx.stroke();
    }
  }
}
